import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import * as quoteApi from './quoteApi';
import { Quote } from './quoteApi';

interface FinalisedQuotesViewProps {
  quotes: Quote[];
  onOpen: (quote: Quote) => void;
  onCopy: (quote: Quote, event: React.MouseEvent) => void;
}

const hours = (quote: Quote) => {
  const value = quote.labour?.final_hrs;
  return typeof value === 'number' ? `${value.toFixed(2)} hrs` : '—';
};

const FinalisedQuotesView: React.FC<FinalisedQuotesViewProps> = ({ quotes, onOpen, onCopy }) => {
  const [query, setQuery] = useState('');
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const locked = quotes.filter((quote) => quote.status === 'finalised');
    if (!needle) return locked;
    return locked.filter((quote) =>
      [quote.quote_ref, quote.customer, quote.notes, quote.cost_centre, quote.source, quote.created_by, quote.updated_by, quote.finalised_by]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [quotes, query]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6">Finalised quotes</Typography>
          <Typography variant="body2" color="text.secondary">
            Locked estimates. Open to review or export. Copy creates an editable new version.
          </Typography>
        </Box>
        <TextField
          size="small"
          label="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 240 }}
        />
      </Box>

      {rows.length === 0 ? (
        <Alert severity="info">
          {quotes.some((quote) => quote.status === 'finalised')
            ? 'No finalised quotes match that search.'
            : 'No finalised quotes yet. Mark an estimate as final on the Final step to archive it here.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Quote</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Cost centre</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell align="right">Lines</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell>Last modified by</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Finalised</TableCell>
                <TableCell>By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((quote) => (
                <TableRow key={quote.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(quote)}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {quote.quote_ref}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      v{quote.version}
                    </Typography>
                    <Chip size="small" label="finalised" color="success" sx={{ ml: 1 }} />
                  </TableCell>
                  <TableCell>{quote.customer || '—'}</TableCell>
                  <TableCell>{quote.notes || '—'}</TableCell>
                  <TableCell>{quote.source || '—'}</TableCell>
                  <TableCell>{quote.cost_centre || '—'}</TableCell>
                  <TableCell align="right">{hours(quote)}</TableCell>
                  <TableCell align="right">{quote.line_count}</TableCell>
                  <TableCell>{quote.created_by || '—'}</TableCell>
                  <TableCell>{quote.updated_by || '—'}</TableCell>
                  <TableCell>{quote.updated_at ? new Date(quote.updated_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>{quote.finalised_at ? new Date(quote.finalised_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>{quote.finalised_by || '—'}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Open locked quote">
                      <IconButton size="small" onClick={() => onOpen(quote)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy as new version">
                      <IconButton size="small" onClick={(e) => onCopy(quote, e)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="BOM Excel">
                      <IconButton
                        size="small"
                        component="a"
                        href={quoteApi.exportUrl(quote.id, 'bom')}
                        disabled={!quote.enhancement_complete}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Labour Excel">
                      <Button
                        size="small"
                        href={quoteApi.exportUrl(quote.id, 'labour')}
                        startIcon={<FileDownloadIcon />}
                      >
                        Labour
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default FinalisedQuotesView;
